<template>
    <v-dialog
        :value="isOpen"
        max-width="600px"
        transition="slide-y-transition"
        eager
        @keydown.esc="onAbort"
        @click:outside="onAbort"
    >
        <v-card>
            <v-card-title>
                <h1 class="text-subtitle-1">
                    Zgłoś awarię
                </h1>
            </v-card-title>

            <v-card-text>
                <v-select
                    v-model="accident.accidentTypeId"
                    label="Typ awarii"
                    :items="accidentTypes"
                    item-value="id"
                    item-text="name"
                    no-data-text="Nie znaleziono typu awarii"
                    :error-messages="accidentTypeErrors()"
                    outlined
                    dense
                    required
                    @change="$v.accident.accidentTypeId.$touch()"
                />
                <v-select
                    v-model="accident.accidentDepartmentId"
                    label="Dział"
                    :items="displayedAccidentDepartments"
                    item-value="id"
                    item-text="name"
                    no-data-text="Nie znaleziono działu"
                    :error-messages="accidentDepartmentErrors()"
                    outlined
                    dense
                    required
                    @change="$v.accident.accidentDepartmentId.$touch()"
                />
                <v-autocomplete
                    v-if="!isSoftwareErrorType"
                    v-model="accident.machineId"
                    label="Maszyna"
                    :items="machines"
                    :filter="customMachinesFilter"
                    item-value="id"
                    item-text="name"
                    :error-messages="machineTypeErrors()"
                    no-data-text="Nie znaleziono maszyny"
                    outlined
                    dense
                    required
                    @change="$v.accident.machineId.$touch()"
                />
                <v-select
                    v-if="isSoftwareErrorType"
                    v-model="accident.softwareTypeId"
                    label="Rodzaj oprogramowania"
                    :items="softwareTypes"
                    item-value="id"
                    item-text="name"
                    no-data-text="Nie znaleziono rodzaju oprogramowania"
                    :error-messages="softwareTypeErrors()"
                    outlined
                    dense
                    required
                    @change="$v.accident.softwareTypeId.$touch()"
                />
                <v-autocomplete
                    v-if="canUpdateAccidentDepartmentsFields"
                    v-model="accident.reportingPerson"
                    :items="users"
                    :filter="customUsersFilter"
                    :loading="usersSearchLoading"
                    :search-input.sync="usersSearch"
                    label="Osoba zgłaszająca"
                    class="v-text-field--placeholder-dark"
                    dense
                    outlined
                    clearable
                    cache-items
                    return-object
                    item-value="id"
                    :item-text="getReportingPersonLabel"
                    no-data-text="Nie znaleziono"
                    @change="changeReportingPerson"
                    @click:clear="$nextTick(() => accident.reportingPersonId = null)"
                >
                    <template slot="no-data">
                        <v-list-item>
                            <v-list-item-title>
                                <template v-if="!usersSearch">
                                    Wpisz treść aby odnaleźć <strong>użytkownika</strong>.
                                </template>
                                <template v-else-if="usersSearchLoading">
                                    Trwa wyszukiwanie...
                                </template>
                                <template v-else>
                                    Nie znaleziono
                                </template>
                            </v-list-item-title>
                        </v-list-item>
                    </template>
                    <template slot="selection" slot-scope="data">
                        {{ getReportingPersonLabel(data.item || accident.reportingPerson) }}
                    </template>
                    <template slot="item" slot-scope="data">
                        <template v-if="data.item.firstName && data.item.lastName">
                            {{ data.item.firstName }} {{ data.item.lastName }}
                        </template>
                        <template v-else>
                            {{ data.item.username }}
                        </template>
                    </template>
                </v-autocomplete>
                <v-autocomplete
                    v-if="canUpdateAccidentDepartmentsFields && !isSoftwareErrorType"
                    v-model="accident.costCenterNumberId"
                    label="MPK"
                    :items="costCenterNumbers"
                    :search-input.sync="searchCostCenterNumber"
                    item-value="id"
                    item-text="verboseName"
                    no-data-text="Nie znaleziono MPK"
                    outlined
                    dense
                    required
                    @change="searchCostCenterNumber = null"
                    @click:clear="$nextTick(() => profile.costCenterNumberId = null)"
                />
                <v-textarea
                    v-model="accident.description"
                    label="Opis"
                    name="Opis"
                    :error-messages="descriptionErrors()"
                    outlined
                    dense
                    required
                    @input="$v.accident.description.$touch()"
                />
            </v-card-text>

            <v-divider />

            <v-card-actions>
                <v-btn
                    color="error"
                    text
                    :disabled="accidentLoading || actionLoading"
                    data-sentry-label="Anuluj"
                    @click="onAbort()"
                >
                    Anuluj
                </v-btn>
                <v-spacer />
                <v-btn
                    :loading="accidentLoading || actionLoading"
                    color="success"
                    text
                    data-sentry-label="Zapisz"
                    @click="createAccident()"
                >
                    Zapisz
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import customFilters from '@/mixins/customFilters'
import { required, requiredIf } from 'vuelidate/lib/validators'
import { errorHandler, addSentryBreadcrumb } from '@/utils/errorHandler'

const SOFTWARE_ERROR_TYPE_ID = 6
const SOFTWARE_DEPARTMENT_ID = 4

let searchTimer

export default {
    name: 'CreateAccidentDialog',
    mixins: [
        customFilters
    ],
    props: {
        isOpen: {
            type: Boolean
        },
        accident: {
            type: Object,
            default: null
        },
        userProductionSubDepartmentId: {
            type: Number,
            default: null
        },
        actionLoading: {
            type: Boolean
        },
        canUpdateAccidentDepartmentsFields: {
            type: Boolean
        },

    },
    data() {
        return {
            usersSearch: null,
            usersSearchLoading: false,
            users: [],
            searchCostCenterNumber: null
        }
    },
    validations() {
        return {
            accident: {
                accidentTypeId: {
                    required
                },
                accidentDepartmentId: {
                    required
                },
                machineId: {
                    required: requiredIf(function() {
                        return !this.isSoftwareErrorType
                    })
                },
                softwareTypeId: {
                    required: requiredIf(function() {
                        return this.isSoftwareErrorType
                    })
                },
                description: {
                    required
                },
            }
        }
    },
    computed: {
        accidentTypes() {
            return this.$store.state.maintenance.accidents.accidentTypes
        },
        isSoftwareErrorType() {
            return this.isSoftwareErrorAccidentType(this.accident?.accidentTypeId)
        },
        softwareAccidentDepartment() {
            return this.accidentDepartments.find(
                department => department.id === SOFTWARE_DEPARTMENT_ID
            ) || null
        },
        displayedAccidentDepartments() {
            if (!this.isSoftwareErrorType) {
                return this.accidentDepartments
            }

            return this.softwareAccidentDepartment ? [this.softwareAccidentDepartment] : []
        },
        machines() {
            return this.$store.state.maintenance.accidents.machinesBasicData
        },
        softwareTypes() {
            return this.$store.state.maintenance.accidents.softwareTypes
        },
        accidentDepartments() {
            return this.$store.state.maintenance.accidents.accidentDepartments
        },
        accidentLoading() {
            return this.$store.state.maintenance.accidents.accidentLoading
        },
        costCenterNumbers() {
            return this.$store.state.administration.costCenterNumbers.costCenterNumbers
        },
    },
    watch: {
        'accident.accidentTypeId'(newId, oldId) {
            if (oldId === undefined) return
            const wasSoftware = this.isSoftwareErrorAccidentType(oldId)
            const isSoftware = this.isSoftwareErrorAccidentType(newId)
            
            if (isSoftware && !wasSoftware) {
                this.accident.machineId = null
                this.accident.costCenterNumberId = null
                if (this.softwareAccidentDepartment) {
                    this.accident.accidentDepartmentId = this.softwareAccidentDepartment.id
                }
            }
            if (!isSoftware && wasSoftware) {
                this.accident.softwareTypeId = null
            }
        },
        usersSearch(value = null) {
            if (value === null) return

            this.usersSearchLoading = true

            clearTimeout(searchTimer)

            searchTimer = setTimeout(() => {
                if (value) {
                    this.searchUsers(value)
                } else {
                    this.usersSearchLoading = false
                    this.users = []
                }
            }, 500)
        }
    },
    methods: {
        getReportingPersonLabel(person) {
            if (!person || typeof person !== 'object') {
                return ''
            }
            if (person.firstName && person.lastName) {
                return `${person.firstName} ${person.lastName}`.trim()
            }
            if (person.username) {
                return person.username
            }

            return ''
        },
        changeReportingPerson(reportingPerson) {
            if (reportingPerson) {
                this.accident.reportingPersonId = reportingPerson.id
                this.accident.costCenterNumberId = reportingPerson.profile.costCenterNumber?.id
            } else {
                this.accident.reportingPersonId = null
                this.accident.costCenterNumberId = null
            }
        },
        searchUsers(query = '') {
            addSentryBreadcrumb('ui.action', 'Wyszukiwanie użytkownika')

            this.$store.dispatch('administration/findUsers', {
                paginate: '?limit=20',
                query: query
            })
                .then(response => {
                    this.users = response.data.results
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.usersSearchLoading = false
                })
        },
        createAccident() {
            this.$v.$touch()
            if (this.$v.$invalid) {
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: this.$i18n.t('validations.invalidMessage')
                })
            } else {
                this.$emit('create-accident')
                this.$v.$reset()
            }
        },
        onAbort() {
            this.$emit('close-accident-dialog')
            this.$v.$reset()
        },
        isSoftwareErrorAccidentType(accidentTypeId) {
            return accidentTypeId === SOFTWARE_ERROR_TYPE_ID
        },
        customMachinesFilter(machine, searchQuery) {
            const name = machine.name.toLowerCase()
            const search = searchQuery.toLowerCase()

            return name.indexOf(search) > -1
        },
        // Walidacje
        accidentTypeErrors() {
            const errors = []

            if (!this.$v.accident.accidentTypeId.$dirty) return errors
            !this.$v.accident.accidentTypeId.required && errors.push('Proszę wybrać typ awarii.')

            return errors
        },
        machineTypeErrors() {
            const errors = []

            if (!this.$v.accident.machineId.$dirty) return errors
            !this.$v.accident.machineId.required && errors.push('Proszę wybrać maszynę.')

            return errors
        },
        softwareTypeErrors() {
            const errors = []

            if (!this.$v.accident.softwareTypeId.$dirty) return errors
            !this.$v.accident.softwareTypeId.required && errors.push('Proszę wybrać rodzaj oprogramowania.')

            return errors
        },
        accidentDepartmentErrors() {
            const errors = []

            if (!this.$v.accident.accidentDepartmentId.$dirty) return errors
            !this.$v.accident.accidentDepartmentId.required && errors.push('Proszę wybrać dział.')

            return errors
        },
        descriptionErrors() {
            const errors = []

            if (!this.$v.accident.description.$dirty) return errors
            !this.$v.accident.description.required && errors.push('Opis jest wymagany.')

            return errors
        },
    }
}
</script>
